/**
 * What kind of thing a model is, for a list that can run to fifty rows.
 *
 * Two glyphs, not five, and the reason is the design system's own rule for
 * when an icon earns its place: it has to disambiguate a row from its
 * neighbours, and a reader covering the word has to still know what it is.
 * Fit against engagement passes both, because a target and a waveform say
 * "scores who they are" against "scores what they did". Lead Engagement, PQL
 * and MQL are all lead-level behavioural models, so three variations on one
 * person-with-activity mark would fail the second test and be decoration.
 *
 * The specific type is carried by the label beside the glyph, which is where
 * the difference between those three actually lives.
 */
import { ModelType } from '../../../models/ModelItem';
import { MODEL_TYPES_NAMES } from '../../../utils/constants';
import FitNavy from '../../assets/images/brand/icons/simple/fit.svg';
import MqlNavy from '../../assets/images/brand/icons/simple/mql.svg';
import PqlNavy from '../../assets/images/brand/icons/simple/pql.svg';
import LeadNavy from '../../assets/images/brand/icons/simple/lead.svg';
import AccountNavy from '../../assets/images/brand/icons/simple/account.svg';
import FitWhite from '../../assets/images/brand/icons/simple/fit-white.svg';
import MqlWhite from '../../assets/images/brand/icons/simple/mql-white.svg';
import PqlWhite from '../../assets/images/brand/icons/simple/pql-white.svg';
import LeadWhite from '../../assets/images/brand/icons/simple/lead-white.svg';
import AccountWhite from '../../assets/images/brand/icons/simple/account-white.svg';

export type ModelFamily = 'fit' | 'engagement';

/**
 * The icon each family is drawn with, as the `Icon` component's class string.
 *
 * A puzzle piece rather than the obvious target, because the target belongs to
 * Conversions in the admin app and the Setup links in the sidebar now carry
 * the admin's glyphs. Two bullseyes on one screen meaning different things is
 * worse than a slightly less literal mark: this one also happens to be the
 * word itself, a thing that FITS.
 */
export const FAMILY_ICON: Record<ModelFamily, string> = {
  fit: 'far fa-puzzle-piece',
  engagement: 'far fa-waveform-lines',
};

/**
 * The icon for each type, in the help center's line style: one 2px navy
 * stroke and a single coral detail, drawn on a 24px grid so it stays legible
 * at chip size, where the brand pack's detailed icons turn to noise. A white
 * twin for the dark (selected) chip.
 */
export const TYPE_ICON: Record<string, { navy: string; white: string }> = {
  customer_fit: { navy: FitNavy, white: FitWhite },
  mql: { navy: MqlNavy, white: MqlWhite },
  pql2: { navy: PqlNavy, white: PqlWhite },
  pql: { navy: LeadNavy, white: LeadWhite },
  mqa: { navy: AccountNavy, white: AccountWhite },
};

/**
 * The family a type belongs to.
 *
 * Everything that is not customer fit is behavioural, which is the split the
 * product already makes everywhere else: fit reads attributes, the rest read
 * events. Written as "not fit" rather than as a list of the four behavioural
 * types so a new behavioural type lands in the right family without an edit
 * here, and a new FIT type is the one case that needs a thought.
 */
export function familyOf(type: ModelType): ModelFamily {
  return type === 'customer_fit' ? 'fit' : 'engagement';
}

/** The type, as the rest of the app already names it. */
export function typeLabel(type: ModelType): string {
  return MODEL_TYPES_NAMES[type] ?? String(type);
}

/**
 * The types present, in the order the filter bar should offer them.
 *
 * Driven by what the tenant actually has rather than by the full list of five,
 * because a filter for a type with no models is a control that can only ever
 * empty the table. Fit first, then the behavioural types in the order the
 * constants declare them, so the bar does not reshuffle as models come and go.
 */
export function typesPresent(types: ModelType[]): ModelType[] {
  const order = Object.keys(MODEL_TYPES_NAMES) as ModelType[];
  const present = new Set(types);
  return order.filter((type) => present.has(type));
}

/**
 * Whether a model matches a free-text search.
 *
 * Over the name, the id and the type as it is LABELLED, not as it is keyed:
 * someone searching "engagement" means the words on their screen, and `pql`
 * is the word for a thing the page calls Lead Engagement.
 */
export function matchesSearch(
  model: { name?: string; modelId?: number; type: ModelType },
  query: string
): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  const haystack = [
    model.name ?? '',
    String(model.modelId ?? ''),
    typeLabel(model.type),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(trimmed);
}
